/**
 * Data layer for the robot telemetry tab. Takes the robot id and the API call
 * as arguments, so the hook carries no knowledge of which portal it runs in.
 * The selected day and range live in the URL (`?day=&range=`), so a link to
 * "this robot, last Tuesday" opens on last Tuesday.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getDailyTelemetry } from "../../services/robotTelemetry.api";
import { parseDay, parseRange, rangeEnding, todayStr, viewerTimeZone } from "./robotTelemetry";

// Today is always on the strip and its numbers keep moving, so the strip is
// re-read quietly on this interval while the tab is open.
const LIVE_REFRESH_MS = 60_000;

const DEFAULT_API = { getDailyTelemetry };

/** Turn an axios failure into one sentence and a banner tone. */
export const describeError = (err, fallback) => {
  const status = err?.response?.status;
  const serverMessage = err?.response?.data?.message;
  if (!err?.response) {
    return {
      message: "Could not reach the server. Check your connection and try again.",
      kind: "offline",
    };
  }
  if (status === 404) {
    return {
      message: "Telemetry isn't available for this robot on this server yet.",
      kind: "unavailable",
    };
  }
  if (status === 403) {
    return { message: "You don't have access to this robot's telemetry.", kind: "error" };
  }
  return { message: typeof serverMessage === "string" ? serverMessage : fallback, kind: "error" };
};

/**
 * @param {string|null} robotId Mongo _id
 * @param {object} [options]
 * @param {boolean} [options.enabled=true] Skip all work when false — a host
 *   that keeps hidden tabs mounted must stop an inactive one fetching or polling.
 * @param {{getDailyTelemetry: Function}} [options.api]
 */
export default function useRobotTelemetry(robotId, { enabled = true, api = DEFAULT_API } = {}) {
  const tz = useMemo(() => viewerTimeZone(), []);
  const [today, setToday] = useState(() => todayStr());
  const [searchParams, setSearchParams] = useSearchParams();

  const range = parseRange(searchParams.get("range"));
  const span = useMemo(() => rangeEnding(today, range), [today, range]);
  const requested = parseDay(searchParams.get("day"), today);
  // A day outside the visible strip can't be selected on it — fall back to today.
  const day = requested >= span.from ? requested : today;

  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiRef = useRef(api);
  useEffect(() => {
    apiRef.current = api;
  }, [api]);

  // Each fetch takes a ticket; a response that comes back after a newer
  // request started is dropped, so a slow reply can't overwrite a fresh one.
  const ticketRef = useRef(0);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!enabled || !robotId) return;
      const ticket = ++ticketRef.current;
      if (!silent) setLoading(true);
      try {
        const data = await apiRef.current.getDailyTelemetry(robotId, { ...span, tz });
        if (ticket !== ticketRef.current) return;
        setDays(Array.isArray(data?.days) ? data.days : []);
        setError(null);
      } catch (err) {
        if (ticket !== ticketRef.current) return;
        setError(describeError(err, "Could not load telemetry."));
      } finally {
        if (ticket === ticketRef.current && !silent) setLoading(false);
      }
    },
    [enabled, robotId, span, tz]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Quiet live refresh; also rolls `today` over at midnight.
  useEffect(() => {
    if (!enabled || !robotId) return undefined;
    const id = setInterval(() => {
      const now = todayStr();
      if (now === today) load({ silent: true });
      else setToday(now);
    }, LIVE_REFRESH_MS);
    return () => clearInterval(id);
  }, [enabled, robotId, today, load]);

  const updateParams = useCallback(
    (changes) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null) next.delete(key);
            else next.set(key, String(value));
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const selectDay = useCallback(
    (date) => updateParams({ day: date === today ? null : date }),
    [today, updateParams]
  );
  const setRange = useCallback((n) => updateParams({ range: n }), [updateParams]);

  return {
    tz,
    today,
    range,
    day,
    days,
    selected: days.find((d) => d.date === day) || null,
    loading,
    error,
    selectDay,
    setRange,
    reload: () => load(),
  };
}
